import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import TextFilter   from './filters/TextFilter';
import DateFilter   from './filters/DateFilter';
import NumberFilter from './filters/NumberFilter';
import SelectFilter from './filters/SelectFilter';

const FILTER_COMPONENTS = {
  text:   TextFilter,
  date:   DateFilter,
  number: NumberFilter,
  select: SelectFilter,
};

function isValidFilter(filter, col) {
  if (!filter) return false;
  const type = col?.type || 'text';
  if (type === 'date') {
    if (['today', 'last_7d', 'last_30d', 'last_90d'].includes(filter.op)) return true;
    if (filter.op === 'before' || filter.op === 'after') return !!filter.value;
    if (filter.op === 'custom') {
      if (!(filter.value || filter.value2)) return false;
      // Reject an inverted range (end before start) so Apply stays disabled
      // instead of silently returning no rows.
      if (filter.value && filter.value2 && filter.value > filter.value2) return false;
      return true;
    }
    return false;
  }
  if (type === 'text') {
    if (filter.condition !== undefined) {
      if (filter.condition === 'IS NULL') return true;
      return !!filter.value || !!filter.order;
    }
    if (filter.op === 'is_empty' || filter.op === 'not_empty') return true;
    return !!filter.value;
  }
  if (type === 'number') {
    if (filter.op === 'is_empty' || filter.op === 'not_empty') return true;
    if (filter.op === 'between') {
      const ok = filter.value !== '' && filter.value !== undefined && filter.value2 !== '' && filter.value2 !== undefined;
      if (!ok) return false;
      // Reject an inverted range (min > max) so Apply stays disabled.
      return Number(filter.value) <= Number(filter.value2);
    }
    return filter.value !== '' && filter.value !== undefined && filter.value !== null;
  }
  if (type === 'select') {
    return Array.isArray(filter.values) && filter.values.length > 0;
  }
  return true;
}

export default function FilterPanel({ id, col, rect, value, onApply, onClear, onClose }) {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(value);
  const panelRef = useRef(null);

  useEffect(() => {
    const previous = document.activeElement;
    panelRef.current?.querySelector('input, button, select')?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);

  const trapFocus = e => {
    if (e.key !== 'Tab') return;
    const nodes = [...panelRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]')];
    const first = nodes[0], last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
  };

  const PANEL_W = 316; // 308px panel + 8px margin
  const PANEL_H_APPROX = 340; // generous estimate covering all filter types
  const rawLeft = rect?.left ?? 0;
  const rawTop  = (rect?.bottom ?? 0) + 6;
  const left = Math.max(8, Math.min(rawLeft, window.innerWidth  - PANEL_W));
  const top  = Math.max(8, Math.min(rawTop,  window.innerHeight - PANEL_H_APPROX));

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]); // eslint-disable-line

  const FilterComp = FILTER_COMPONENTS[col?.type] || TextFilter;
  const canApply = isValidFilter(localValue, col);

  const btnBase = {
    padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 5,
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s',
  };

  return createPortal(
    <div
      id={id}
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      onKeyDown={trapFocus}
      aria-label={col?.label}
      style={{
        position: 'fixed',
        top,
        left,
        zIndex: 9999,
        width: 308,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: `calc(100vh - ${top + 8}px)`,
        overflowY: 'auto',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        fontSize: 12,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>
          {col?.label}
        </span>
        <button
          aria-label={t('common.close')}
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
        >×</button>
      </div>

      <FilterComp
        col={col}
        value={localValue}
        onChange={setLocalValue}
      />

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onClear}
          style={{
            ...btnBase, flex: 1, padding: '8px 0', textAlign: 'center',
            background: 'transparent', border: '1px solid var(--border)',
            color: 'var(--text-muted)',
          }}
        >
          {t('table.clear')}
        </button>
        <button
          onClick={() => { if (canApply) onApply(localValue); }}
          disabled={!canApply}
          style={{
            ...btnBase, flex: 1, padding: '8px 0', textAlign: 'center',
            background: canApply ? 'var(--accent)' : 'var(--overlay-subtle)',
            border: `1px solid ${canApply ? 'var(--accent)' : 'var(--border)'}`,
            color: canApply ? '#fff' : 'var(--text-muted)',
            cursor: canApply ? 'pointer' : 'not-allowed',
          }}
        >
          {t('table.apply')}
        </button>
      </div>
    </div>, document.body
  );
}
